import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepApplicantElement } from '@models/applicants';
import { Form, Input, Select, Spin, Tooltip } from 'antd';
import StateEnum from '@constants/state.enum';
import OrangeWarning from '@assets/svgs/orange-warning-icon.svg';
import useNotification from '@hooks/notification/useNotification';
import TinyMCEEditor from '@components/ka-ui/tinymce-editor/TinyMCEEditor';
import {
  getEmailTemplateReplaceParams,
  getEmailTemplates,
  sendEmailService,
} from '@services/email-templates/email-templates-http.service';
import { EmailTemplateType } from '@models/email-templates';
import styles from './SendEmail.module.scss';
import UiButton from '@components/ui/button/UiButton';

type SendEmailProps = {
  positionId: string;
  step: string;
  applicant: StepApplicantElement;
  setOpenEmailTemplatesModal: (value: boolean) => void;
  setShowSendEmailForm: (value: boolean) => void;
  fetchApplicants: () => void;
};

const { Option } = Select;

const SendEmail: React.FC<SendEmailProps> = ({
  step,
  applicant,
  positionId,
  setOpenEmailTemplatesModal,
  setShowSendEmailForm,
  fetchApplicants,
}) => {
  const { t: translate } = useTranslation(['applicantCard', 'common']);
  const [emailTemplates, setEmailTemplates] = useState<Array<EmailTemplateType>>();
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState<string>();
  const [selectLoading, setSelectLoading] = useState<boolean>(false);
  const [emptyBodyMessage, setEmptyBodyMessage] = useState<boolean>(true);
  const [exceedingCharacters, setExceedingCharacters] = useState<boolean>(false);
  const [templateEmail, setTemplateEmail] = useState<EmailTemplateType>();
  const [sendEmailForm] = Form.useForm();

  const [generalError] = useNotification(
    StateEnum.ERROR,
    translate('common:smth_went_wrong'),
    translate('common:oops_wrong'),
  );
  const [successSend] = useNotification(StateEnum.SUCCESS, translate('email_send_successfully'));

  const fetchEmailTemplates = async () => {
    setSelectLoading(true);
    try {
      const emailTemplatesElem = await getEmailTemplates();
      setEmailTemplates(emailTemplatesElem);
      await setSelectLoading(false);
    } catch (e: any) {
      generalError();
      setSelectLoading(false);
    }
  };

  const fetchEmailTemplate = async (id: string) => {
    try {
      const emailTemplateElem: EmailTemplateType = await getEmailTemplateReplaceParams(
        id,
        positionId,
        applicant.id.toString(),
      );
      setTemplateEmail(emailTemplateElem);
      sendEmailForm.setFieldValue('subject', emailTemplateElem.subject);
      sendEmailForm.setFieldValue('body', emailTemplateElem.body);
      setSelectedEmailTemplateId(id);
    } catch (e: any) {
      generalError();
    }
  };

  const sendEmail = async (values: any) => {
    try {
      await sendEmailService(
        positionId,
        parseInt(applicant.id.toString()),
        selectedEmailTemplateId!,
        values.subject,
        values.body,
        parseInt(applicant.stepId),
      );
      sendEmailForm.resetFields();
      setTemplateEmail(undefined);
      setOpenEmailTemplatesModal(false);
      setSelectedEmailTemplateId(undefined);
      setEmptyBodyMessage(true);
      setShowSendEmailForm(false);
      successSend();
      fetchApplicants();
    } catch (e: any) {
      generalError();
    }
  };

  const validateWhitespace = (rule: any, value: any) => {
    if (value && /^\s+$/.test(value)) {
      return Promise.reject(<div className={`h6 ${styles.warningMessage}`}>{translate('please_subject')}</div>);
    }
    return Promise.resolve();
  };

  return (
    <div className={styles.wrapSendEmailForm}>
      <div className={`large-body-text ${styles.sentToOnStep}`}>
        {translate('sent_to')}
        <div className={`h5 ${styles.lightGreySubtitle}`}>“{`${applicant.firstName} ${applicant.lastName}`}”</div>
        {translate('on_step')}
        <div className={`h5 ${styles.lightGreySubtitle}`}>“{step}”</div>
      </div>
      <Form form={sendEmailForm} className={styles.wrapSendEmailMainForm} onFinish={sendEmail}>
        <Form.Item name="selectEmail" className={`antd-overwrite ${styles.wrapSelectTemplateField}`}>
          <div className={`h6 ${styles.templateTitleField}`}>{translate('template_title')}</div>
          <Select
            placeholder={translate('select_email')}
            optionFilterProp="children"
            value={selectedEmailTemplateId}
            onClick={() => fetchEmailTemplates()}
            onChange={async (value) => {
              await fetchEmailTemplate(value);
            }}
            data-testId="select-email-template"
          >
            {selectLoading ? (
              <Option data-testid="spinner-option">
                <Spin size="default" />
              </Option>
            ) : (
              emailTemplates &&
              emailTemplates.map((emailTemplate) => {
                return (
                  <Option key={emailTemplate.id} value={emailTemplate.id}>
                    {emailTemplate.title}
                  </Option>
                );
              })
            )}
          </Select>
        </Form.Item>
        <div className={styles.toField}>
          <div className={`large-body-text ${styles.toLabel}`}>{translate('to')}</div>
          <div className={`large-body-text ${styles.toContent}`}>
            {applicant.email ? (
              `${applicant.email}`
            ) : (
              <Tooltip
                placement="top"
                mouseLeaveDelay={0.1}
                color="#3e4b5e"
                title={<div className="large-body-text">{translate('no_email_add_it')}</div>}
                trigger="hover"
              >
                <div className={styles.orangeWarningIcon}>
                  <OrangeWarning />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <div className={styles.subjectField}>
          <div className={`large-body-text ${styles.subjectLabel}`}>{translate('subject')}</div>
          <Form.Item
            name="subject"
            rules={[
              {
                required: true,
                message: <div className={`h6 ${styles.warningMessage}`}>{translate('please_subject')}</div>,
              },
              {
                max: 150,
                message: <div className={`h6 ${styles.warningMessage}`}>{translate('too_many_characters')}</div>,
              },
              { validator: validateWhitespace },
            ]}
          >
            <Input className={`large-body-text ${styles.subjectContent}`} placeholder={translate('give_subject')} />
          </Form.Item>
        </div>
        <Form.Item
          name="body"
          className={emptyBodyMessage || exceedingCharacters ? styles.wrapEmptyBodyField : styles.wrapBodyField}
        >
          <TinyMCEEditor
            id={`${applicant.id}-body-send-email-editor`}
            toolbar="bold italic underline|bullist numlist alignleft aligncenter alignright|link hr "
            placeholder="Βody"
            value={templateEmail?.body}
            initialValue={templateEmail?.body}
            height={327}
            onValueChange={(value) => {
              if (/^\s*$/.test(new DOMParser().parseFromString(value, 'text/html').body.textContent!)) {
                setEmptyBodyMessage(true);
              } else {
                setEmptyBodyMessage(false);
              }
              setExceedingCharacters(
                (new DOMParser().parseFromString(value, 'text/html').body.textContent || '').length > 2000,
              );
              sendEmailForm.setFieldsValue({ body: value });
            }}
          />
        </Form.Item>
        {emptyBodyMessage && <div className={`h6 ${styles.warningMessage}`}>{translate('please_body')}</div>}
        {exceedingCharacters && <div className={`h6 ${styles.warningMessage}`}>{translate('max_chars')}</div>}
        <div className={styles.wrapSendEmailActions}>
          <UiButton
            type="primary"
            size="small"
            htmlType="submit"
            disabled={!applicant.email?.length || emptyBodyMessage || exceedingCharacters}
          >
            {translate('send_email')}
          </UiButton>
          <UiButton type="default" size="small" onClick={() => setOpenEmailTemplatesModal(false)}>
            {translate('cancel')}
          </UiButton>
        </div>
      </Form>
    </div>
  );
};

export default SendEmail;
